/**
 * HistoryEntry
 * Single entry in the issue history timeline
 */

import { makeStyles, tokens, Text, Avatar } from '@fluentui/react-components'
import {
  ArrowSwapRegular,
  PersonSwapRegular,
  CommentRegular,
  EditRegular,
  ArrowUpRegular,
} from '@fluentui/react-icons'
import type { IssueHistoryEntry } from '../../../../domain'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalM,
    position: 'relative',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '32px',
    flexShrink: 0,
  },
  iconContainer: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  icon: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  line: {
    flex: 1,
    width: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: tokens.spacingVerticalXS,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  actorName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    color: tokens.colorNeutralForeground2,
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  details: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalXS,
  },
})

const getIconForType = (type: IssueHistoryEntry['type']) => {
  switch (type) {
    case 'status_change':
      return <ArrowSwapRegular />
    case 'assignment':
      return <PersonSwapRegular />
    case 'comment':
      return <CommentRegular />
    case 'update':
      return <EditRegular />
    case 'escalation':
      return <ArrowUpRegular />
    default:
      return <EditRegular />
  }
}

export interface HistoryEntryProps {
  entry: IssueHistoryEntry
  showLine?: boolean
}

export function HistoryEntry({ entry, showLine = true }: HistoryEntryProps) {
  const styles = useStyles()

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <div className={styles.root}>
      <div className={styles.timeline}>
        <div className={styles.iconContainer}>
          <span className={styles.icon}>{getIconForType(entry.type)}</span>
        </div>
        {showLine && <div className={styles.line} />}
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <Avatar name={entry.actor.displayName} size={20} />
          <Text size={200} className={styles.actorName}>
            {entry.actor.displayName}
          </Text>
          <Text size={200} className={styles.description}>
            {entry.description}
          </Text>
        </div>
        <Text className={styles.timestamp}>{formatTimestamp(entry.timestamp)}</Text>
        {entry.details && (
          <Text size={200} className={styles.details}>
            {entry.details}
          </Text>
        )}
      </div>
    </div>
  )
}
